import React, { useState, useEffect } from "react";
import { LdButton } from "@liquid/react";
import { FormSelect } from "@src/components";
import { ButtonTab } from "@src/components/ButtonTab";
import { FormRadioButtonGroup } from "@src/components/FormRadioButtonGroup";
import { FormSlider } from "@src/components/FormSider";
import {
  BSE_EDITOR_TYPE,
  CBE_TYPE,
  STOP_CODON_KEYS,
} from "@src/constants";
import { StyledColumn, StyledRow } from "@src/styles";
import { SequenceInputFormProps } from "@src/types";
import { FormProvider, useForm } from "react-hook-form";
import { StyledForm } from "./SequenceInputForm.style";
import { StyledButtonsContainer } from "@src/pages/NotFoundPage/NotFoundPage.style";
import { useGetGenomeAssemblies } from "@src/query/editor/EditorQuery";
import { useNotification } from "@src/hooks/useNotification";

type FormValues = {
  edit_window: string;
  genome: string;
  user_input_sequence: string;
  user_id: string;
  project_id: string;
  base_editor_type: string;
  cbe_type: string;
  grna_length: string;
  pam_type: string;
  genome_specific_editing: string;
};

function SequenceInputForm({
  onSubmit,
  onClear,
  transcriptLen,
  loading,
  onGenomeSpecificChange
}: SequenceInputFormProps) {
  const [cbeType, setCbeType] = useState("flexible");
  const [clearButtonClick, setClearButtonClick] = useState(false);
  const [genomeOptions, setGenomeOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [isGenomeSpecific, setIsGenomeSpecific] = useState<boolean>(true);

  const { showNotification } = useNotification();

  // Fetch genome assemblies using the query hook
  const {
    data: genomeData,
    isLoading: isLoadingGenomes,
    error: genomeError,
  } = useGetGenomeAssemblies();

  const methods = useForm<FormValues>({
    defaultValues: {
      edit_window: "3,15",
      base_editor_type: "cbe",
      cbe_type: "flexible",
      user_id: process.env.REACT_APP_USER_ID || "user_1",
      user_input_sequence: "",
      grna_length: "20",
      pam_type: "NGG",
      genome: "",
      genome_specific_editing: "yes",
    },
  });

  // Transform API response to dropdown options
  useEffect(() => {
    if (genomeData?.genome_assemblies && genomeData.genome_assemblies.length > 0) {
      const options = genomeData.genome_assemblies.map((genome) => ({
        value: genome.assembly,
        label: genome.display_name,
      }));
      setGenomeOptions(options);

      // Set first option as default if no genome is selected and genome specific is enabled
      if (options.length > 0 && !methods.getValues("genome") && isGenomeSpecific) {
        methods.setValue("genome", options[0].value);
      }
    }
  }, [genomeData, methods, isGenomeSpecific]);

  // Handle genome loading error
  useEffect(() => {
    if (genomeError) {
      showNotification({
        content: (genomeError as any)?.message || "Failed to load genome assemblies",
        type: "alert",
      });
    }
  }, [genomeError, showNotification]);

  useEffect(() => {
    onClear();
  }, []);

  const onCodonChange = (value: any) => {
    STOP_CODON_KEYS.map((key: any) => {
      return methods.setValue(key, value[key]);
    });
  };

  const tmpSetCBEType = (v: string) => {
    setCbeType(v);
    methods.setValue("edit_window", v === "flexible" ? "3,15" : "3,9");
    onResetClick();
  };

  // Handler for genome specific editing
  const handleGenomeSpecificChange = (value: string) => {
    const isYes = value === "yes";
    setIsGenomeSpecific(isYes);
    onGenomeSpecificChange?.(isYes); // ✅ notify parent

    if (!isYes) {
      // Clear genome selection if "No" is selected
      methods.setValue("genome", "");
    } else if (genomeOptions.length > 0) {
      // Set first option if "Yes" is selected and no genome is selected
      if (!methods.getValues("genome")) {
        methods.setValue("genome", genomeOptions[0].value);
      }
    }
  };

  const onResetClick = () => {
    methods.resetField("user_input_sequence");
    setClearButtonClick(true);
    setTimeout(() => {
      setClearButtonClick(false);
    }, 2000);
    if (onClear && typeof onClear === "function") {
      onClear();
    }
  };

  return (
    <FormProvider {...methods}>
      <StyledForm onSubmit={methods.handleSubmit(onSubmit)}>
        {/* Genome Specific Editing Radio Buttons */}
        <StyledRow>
          <StyledColumn width={100}>
            <FormRadioButtonGroup
              name="genome_specific_editing"
              label="Genome specific editing"
              options={[
                { value: "yes", label: "Yes" },
                { value: "no", label: "No" },
              ]}
              errorMessage="This field is required."
              onCBEChange={handleGenomeSpecificChange}
              horizontal={true}// ADD THIS PROP - makes it horizontal
            />
          </StyledColumn>
        </StyledRow>

        {/* Target Genome Select - Only show if genome specific editing is Yes */}
        {isGenomeSpecific && (
          <StyledRow>
            <StyledColumn width={100}>
              <FormSelect
                name="genome"
                label="Target Genome"
                options={genomeOptions}
                placeholder={isLoadingGenomes ? "Loading genomes..." : "Select Genome"}
                disabled={isLoadingGenomes}
                rules={{
                  required: {
                    value: isGenomeSpecific,
                    message: "This is a required field.",
                  },
                }}
              />
            </StyledColumn>
          </StyledRow>
        )}

        <StyledRow gap={10}>
          <StyledColumn width={50}>
            <FormRadioButtonGroup
              name="base_editor_type"
              label="Base Editor Type"
              options={BSE_EDITOR_TYPE}
              errorMessage="This field is required."
            />
          </StyledColumn>
          <StyledColumn width={50}>
            <FormRadioButtonGroup
              name="cbe_type"
              label="CBE Type"
              options={CBE_TYPE}
              errorMessage="This field is required."
              onCBEChange={tmpSetCBEType}
            />
          </StyledColumn>
        </StyledRow>

        <StyledRow gap={10}>
          <StyledColumn width={100}>
            <FormSlider
              max={20}
              min={1}
              label="Base Editing Window"
              name={"edit_window"}
              rules={{
                required: {
                  value: true,
                  message: "This is a required field.",
                },
              }}
              beWindow={cbeType}
            />
          </StyledColumn>
        </StyledRow>

        {/* ButtonTab - Pass isGenomeSpecific to hide stop codon tab */}
        <ButtonTab
          onCodonChange={onCodonChange}
          genome={isGenomeSpecific ? methods.getValues("genome") : ""}
          clearButtonClick={clearButtonClick}
          onResetClick={onResetClick}
          isGenomeSpecific={isGenomeSpecific}
        />

        <StyledButtonsContainer>
          {isGenomeSpecific && (
            <LdButton
              type="button"
              className="reset-button"
              style={{ width: "18rem" }}
              onClick={onResetClick}
              disabled={!transcriptLen || loading}
            >
              Clear
            </LdButton>
          )}
          <LdButton
            type="submit"
            style={{ width: "18rem" }}
            disabled={transcriptLen || loading || isLoadingGenomes}
          >
            {isGenomeSpecific ? "Show Transcript" : "Submit"}
          </LdButton>
        </StyledButtonsContainer>
      </StyledForm>
    </FormProvider>
  );
}

export { SequenceInputForm };
